import React, { useCallback } from "react";

import MainPageHOC from "../../layout/hoc/MainPageHOC";
import MainPageTableHeader from "../../layout/body/MainPageTableHeader";
import PaginationComponent from "../../components/pagination/PaginationComponent";
import FetcherComponent from "../../components/shared/FetcherComponent";
import MainPageHeader from "../../components/shared/MainPageHeader";

import userService from "../../services/userService";
import { useUserPageContext } from "../../hooks/useUserPageContext";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { USER_ROLES, SEARCH_PARAM_KEY } from "../../utilities/constants";
import UsersPageTableBody from "../../components/userPage/UsersPageTableBody";

const canManageUsers = (roles) =>
  roles?.some((r) => r === USER_ROLES.DEVELOPER || r === USER_ROLES.ADMIN) ??
  false;

const UsersPage = () => {
  const { user } = useAuthContext();
  const showCreateButton = canManageUsers(user?.roles);

  const {
    users,
    setUsers,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalRecords,
    setTotalRecords,
    setPageSize,
    pageSize,
  } = useUserPageContext();

  const { search, setSearch, searchQuery, searchParams } = useUrlFilters({
    searchParamKey: SEARCH_PARAM_KEY,
  });

  const currentPageQueryString = searchParams.get("current-page");
  const pageSizeQueryString = searchParams.get("page-size");
  const fetchUsers = useCallback(
    async () =>
      await userService.getUsers({
        currentPage: currentPageQueryString,
        pageSize: pageSizeQueryString,
        search: searchQuery,
      }),
    [currentPageQueryString, pageSizeQueryString, searchQuery]
  );

  const handleOnLoadFetchedUsers = useCallback(
    (res) => {
      setUsers(res?.data);
      setCurrentPage(res?.currentPage);
      setTotalPages(res?.totalPages);
      setTotalRecords(res?.totalRecords);
      setPageSize(res?.pageSize);
    },
    [setUsers, setCurrentPage, setTotalPages, setTotalRecords, setPageSize]
  );

  const userDataChecker = useCallback(
    () => (users?.length > 0 ? true : false),
    [users]
  );

  return (
    <MainPageHOC
      headerTitle="Users"
      pageHeader={
        <MainPageHeader
          totalRecords={totalRecords}
          pageName="Users"
          redirectTo="/users/create"
          showAddButton={showCreateButton}
        />
      }
      tableHeader={
        <MainPageTableHeader
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchValue={search}
          onSearchChange={setSearch}
        />
      }
      tableBody={
        <FetcherComponent
          key={[currentPageQueryString, pageSizeQueryString, searchQuery]
            .filter(Boolean)
            .join("-")}
          action={fetchUsers}
          onLoad={handleOnLoadFetchedUsers}
        >
          <>
            <UsersPageTableBody users={users} />
            <div className="card-inner">
              <PaginationComponent
                currentPageNumber={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                status={pageSizeQueryString}
              />
            </div>
          </>
        </FetcherComponent>
      }
      hasDataForRendering={userDataChecker}
    />
  );
};

export default UsersPage;
